import { call } from '@advanza/api'
import { useEffect, useState } from 'react'
import { HEALTH_SCORE_ENDPOINT } from './healthScoreConstants'
import type { GetHealthScoreResponse, HealthScore } from './types'

interface HealthScoreState {
    data: HealthScore | null
    isLoading: boolean
    error: string | null
}

const cache = new Map<number, Promise<HealthScore | null>>()

const fetchHealthScore = (providerId: number): Promise<HealthScore | null> => {
    let promise = cache.get(providerId)

    if (!promise) {
        promise = call(`${HEALTH_SCORE_ENDPOINT}/${providerId}`)
            .then((response: GetHealthScoreResponse) => response.healthScore ?? null)
            .catch((error) => {
                cache.delete(providerId)
                throw error
            })
        cache.set(providerId, promise)
    }

    return promise
}

export const useHealthScore = (providerId: number): HealthScoreState => {
    const [state, setState] = useState<HealthScoreState>({
        data: null,
        isLoading: true,
        error: null,
    })

    useEffect(() => {
        let cancelled = false
        setState({ data: null, isLoading: true, error: null })

        fetchHealthScore(providerId)
            .then((data) => {
                if (!cancelled) {
                    setState({ data, isLoading: false, error: null })
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    setState({
                        data: null,
                        isLoading: false,
                        error: error?.error || error?.message || 'Failed to load health score',
                    })
                }
            })

        return () => {
            cancelled = true
        }
    }, [providerId])

    return state
}
